/**
 * Qué archivo se va a usar para armar el presupuesto.
 *
 * POR QUÉ EXISTE ESTA FUNCIÓN, Y POR QUÉ ES UNA SOLA. Antes había dos criterios que no
 * coincidían, escritos a diez líneas de distancia en la misma pantalla:
 *
 *   const source = pickCatalogFile(files) ?? files[0];   // lo que el botón USABA
 *   const proposedCatalog = pickCatalogFile(files);      // lo que la pantalla MOSTRABA
 *
 * Cuando ningún archivo detectaba conceptos, el desplegable se quedaba vacío diciendo
 * "Selecciona el archivo del catálogo…" y el botón, igual, armaba el presupuesto con el
 * primero de la lista. Le pedía a la persona que eligiera y elegía solo, sin decir cuál.
 * Y el resultado no es un error: es un presupuesto con los conceptos de otro documento.
 *
 * Ahora hay una sola respuesta, y viene con el MOTIVO, para que la pantalla pueda decir
 * de dónde salió. La regla de fondo: nunca usar un archivo que no se haya nombrado.
 *
 * OJO CON `conceptsDetected`. Se calcula sin IA (el extractor barato sobre el texto, $0),
 * así que un catálogo en PDF que solo el respaldo con IA del worker sabe leer da CERO y
 * es perfectamente cotizable. Por eso no detectar conceptos no puede bloquear nada: solo
 * cambia lo que se le dice al usuario.
 */

/** Lo mínimo que se necesita de un archivo. `ProjectFile` encaja. */
export interface ArchivoProponible {
  id: string;
  originalName: string;
  classification?: string | null;
  conceptsDetected?: number | null;
  uploadedAt?: string;
}

export type MotivoDeLaPropuesta =
  /** Lo marcó la persona. Manda siempre. */
  | 'elegido'
  /** Es el que más conceptos tiene. */
  | 'mas-conceptos'
  /** Ninguno detectó conceptos: es una corazonada nuestra y hay que decirlo. */
  | 'corazonada';

export interface PropuestaDeCatalogo<T extends ArchivoProponible> {
  archivo: T;
  motivo: MotivoDeLaPropuesta;
}

export function proponerCatalogo<T extends ArchivoProponible>(
  archivos: readonly T[],
): PropuestaDeCatalogo<T> | null {
  if (archivos.length === 0) return null;

  const elegido = archivos.find((f) => f.classification === 'catalogo');
  if (elegido) return { archivo: elegido, motivo: 'elegido' };

  const conConceptos = archivos
    .filter((f) => (f.conceptsDetected ?? 0) > 0)
    .sort((a, b) => (b.conceptsDetected ?? 0) - (a.conceptsDetected ?? 0));
  if (conConceptos[0]) return { archivo: conConceptos[0], motivo: 'mas-conceptos' };

  // Nadie detectó conceptos. Antes acá se usaba el PRIMERO de la lista, que viene
  // ordenada por fecha de subida ascendente: o sea, el MÁS VIEJO. Es la peor corazonada
  // posible — quien acaba de subir un archivo lo subió por algo, y el caso real que
  // motivó todo esto fue precisamente alguien reemplazando un catálogo que no se dejaba
  // leer. Se propone el más RECIENTE, y la pantalla dice que es una corazonada.
  const masReciente = [...archivos].sort(porFechaDesc)[0]!;
  return { archivo: masReciente, motivo: 'corazonada' };
}

/** Más reciente primero. Sin fecha va al final; empate, se respeta el orden recibido. */
function porFechaDesc(a: ArchivoProponible, b: ArchivoProponible): number {
  const fa = a.uploadedAt ? Date.parse(a.uploadedAt) : NaN;
  const fb = b.uploadedAt ? Date.parse(b.uploadedAt) : NaN;
  if (Number.isNaN(fa) && Number.isNaN(fb)) return 0;
  if (Number.isNaN(fa)) return 1;
  if (Number.isNaN(fb)) return -1;
  return fb - fa;
}
